import * as XState from 'xstate'
import * as SubmissionClient from 'clients/min/submission'
import * as File from '@avcan/utils/file'

export default XState.Machine({
    id: 'upload-images-process',
    initial: 'idle',
    states: {
        idle: {
            entry: XState.assign(() => {
                return {
                    files: new Map(),
                    processes: [],
                    images: null,
                }
            }),
            on: {
                ADD: {
                    target: 'post',
                    actions: XState.assign({
                        files(context, event) {
                            const files = Array.from(event.files, file => [file.name, file])

                            return new Map(files.concat(Array.from(context.files)))
                        },
                    }),
                },
                REMOVE: {
                    actions: 'remove',
                },
                MOVE_PREVIOUS: {
                    actions: 'movePrevious',
                },
                MOVE_NEXT: {
                    actions: 'moveNext',
                },
            },
        },
        post: {
            initial: 'pending',
            on: {
                'POST.SUCCESS': {
                    target: 'upload',
                    actions: XState.assign({
                        images(context, event) {
                            return event.images
                        },
                    }),
                },
            },
            states: {
                pending: {
                    invoke: {
                        src: 'postImages',
                        onDone: 'success',
                        onError: 'failure',
                    },
                },
                success: {
                    type: 'final',
                    entry: XState.send((context, event) => ({
                        type: 'POST.SUCCESS',
                        images: event.data,
                    })),
                },
                failure: {
                    on: {
                        RETRY: 'pending',
                    },
                },
            },
        },
        upload: {
            on: {
                'UPLOAD.SUCCESS': {
                    actions: [
                        XState.assign({
                            processes(context, event) {
                                return context.processes.filter(
                                    process => process.machine.id !== event.image.id
                                )
                            },
                        }),
                        'append',
                    ],
                },
                'UPLOAD.BAIL': {
                    actions: XState.assign({
                        processes(context, event) {
                            const { processes } = context
                            const process = processes.find(
                                process => process.machine.id === event.id
                            )

                            process.stop()

                            return processes.filter(process => process.machine.id !== event.id)
                        },
                    }),
                },
            },
            entry: XState.assign({
                processes(context) {
                    const { files } = context

                    return context.images.map(image => {
                        const file = files.get(image.filename)
                        const machine = UploadImageMachine.withContext({ file, image })

                        machine.id = image.id

                        return XState.spawn(machine, { sync: true })
                    })
                },
            }),
            always: [
                {
                    target: 'success',
                    cond(context) {
                        return context.processes.length === 0
                    },
                },
            ],
        },
        success: {
            after: {
                2500: {
                    target: 'idle',
                },
            },
        },
    },
})

const UploadImageMachine = XState.Machine(
    {
        id: 'upload-image',
        initial: 'read',
        states: {
            read: {
                initial: 'pending',
                states: {
                    pending: {
                        invoke: {
                            src: 'read',
                            onDone: {
                                target: 'success',
                                actions: XState.assign({
                                    url(context, event) {
                                        return event.data
                                    },
                                }),
                            },
                            onError: 'failure',
                        },
                    },
                    success: {
                        type: 'final',
                    },
                    failure: {
                        on: {
                            RETRY: {
                                target: 'pending',
                            },
                        },
                    },
                },
                onDone: 'upload',
            },
            upload: {
                initial: 'pending',
                entry: XState.assign({
                    retries() {
                        return 0
                    },
                }),
                states: {
                    pending: {
                        invoke: {
                            src: 'upload',
                            onDone: 'success',
                            onError: 'failure',
                        },
                    },
                    success: {
                        type: 'final',
                    },
                    failure: {
                        on: {
                            RETRY: {
                                target: 'pending',
                                actions: XState.assign({
                                    retries(context) {
                                        return context.retries + 1
                                    },
                                }),
                            },
                            REMOVE: {
                                actions: XState.sendParent(context => {
                                    return {
                                        type: 'UPLOAD.BAIL',
                                        to: 'upload-images-process',
                                        id: context.image.id,
                                    }
                                }),
                            },
                        },
                    },
                },
                onDone: 'success',
            },
            success: {
                type: 'final',
                entry: XState.sendParent(context => {
                    return {
                        type: 'UPLOAD.SUCCESS',
                        to: 'upload-images-process',
                        image: context.image,
                    }
                }),
            },
        },
    },
    {
        services: {
            read(context) {
                return File.read(context.file)
            },
            upload(context) {
                const { file, image } = context

                return SubmissionClient.uploadImage(image.uploadUrl, file)
            },
        },
    }
)
